<template>
  <div class="qr-scanner-wrapper">
    <select
      v-if="Array.isArray(cameras) && cameras.length > 1"
      v-model="selectedCameraId"
      class="w-full max-w-md mb-2"
      @change="onCameraChange"
    >
      <option v-for="cam in cameras" :key="cam.id" :value="cam.id">
        {{ cam.label || 'Camera ' + cam.id.slice(0, 8) }}
      </option>
    </select>
    <div v-if="loading" class="p-3">Loading camera…</div>
    <div v-if="error" class="p-3 text-red-600">{{ error }}</div>
    <div class="qr-scanner-container" id="qr-reader"></div>
    <Modal
      v-if="identifiedId"
      title="Edit Box"
      @closeModal="closeEditModal"
    >
      <template #content>
        <EditBox
          :equipmentId="identifiedId"
          :allowEdit="true"
          :labelView="true"
          showWarehouseActions
          @updateEquipment="closeEditModal"
        />
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import EditBox from '@/components/projects/equipment/menuOptions/EditBox.vue'

// Must fit inside viewfinder or library hides the box; on 375×667 height can be ~210px
const QRBOX_SIZE = 200
const SUCCESS_SOUND = '/sounds/short-success-sound.mp3'
const SCAN_COOLDOWN_MS = 2000

/** MongoDB-style ObjectId (24 hex chars). */
const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i

export default {
  components: { EditBox },
  setup() {
    const loading = ref(true)
    const error = ref(null)
    const cameras = ref([])
    const selectedCameraId = ref('')
    const identifiedId = ref(null)
    let html5QrCode = null
    let lastDecodedText = ''
    let lastDecodedAt = 0

    function isValidMongooseId(id) {
      return typeof id === 'string' && OBJECT_ID_REGEX.test(id)
    }

    function getLabelId(decodedText) {
      const raw = decodedText?.trim()
      if (!raw) return null

      const looksLikeUrl = /^https?:\/\//i.test(raw)
      if (looksLikeUrl) {
        try {
          const url = new URL(raw)
          const segments = url.pathname.split('/').filter(Boolean)
          const lastSegment = segments[segments.length - 1]
          if (segments[segments.length - 2] === 'boxLabel' && lastSegment && OBJECT_ID_REGEX.test(lastSegment)) {
            return lastSegment
          }
          const fromQuery = url.searchParams.get('id')
          if (fromQuery && OBJECT_ID_REGEX.test(fromQuery)) return fromQuery
          const fromPath = segments.find((s) => OBJECT_ID_REGEX.test(s))
          if (fromPath) return fromPath
          if (fromQuery) return fromQuery
        } catch {
          return null
        }
        return null
      }

      try {
        const data = JSON.parse(raw)
        const id = data?.id ?? data?._id
        if (id != null && typeof id === 'string') return id
      } catch {
        // not JSON
      }
      return null
    }

    function closeEditModal() {
      identifiedId.value = null
    }

    function playSuccessSound() {
      const audio = new Audio(SUCCESS_SOUND)
      audio.volume = 1
      audio.play().catch(() => {})
    }

    /** Get system default video device id (browser chooses when no constraints). */
    async function getDefaultCameraId() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        const track = stream.getVideoTracks()[0]
        const deviceId = track?.getSettings?.()?.deviceId
        track?.stop()
        stream.getTracks().forEach((t) => t.stop())
        return deviceId || null
      } catch {
        return null
      }
    }

    async function startWithCamera(cameraIdOrConfig) {
      const config = {
        fps: 20,
        qrbox: { width: QRBOX_SIZE, height: QRBOX_SIZE },
      }
      const onScan = (decodedText) => {
        if (identifiedId.value) return
        const now = Date.now()
        if (decodedText === lastDecodedText && now - lastDecodedAt < SCAN_COOLDOWN_MS) return
        const _id = getLabelId(decodedText)
        if (_id == null) return
        if (!isValidMongooseId(_id)) {
          lastDecodedText = decodedText
          lastDecodedAt = now
          error.value = 'Invalid box ID (not a valid format).'
          return
        }
        lastDecodedText = decodedText
        lastDecodedAt = now
        error.value = null
        identifiedId.value = _id
        playSuccessSound()
      }
      try {
        await html5QrCode.start(cameraIdOrConfig, config, onScan, () => {})
      } catch (err) {
        error.value = err?.message || 'Could not start camera.'
      } finally {
        loading.value = false
      }
    }

    async function onCameraChange() {
      if (!selectedCameraId.value) return
      loading.value = true
      error.value = null
      if (html5QrCode?.isScanning) {
        await html5QrCode.stop().catch(() => {})
      }
      const container = document.getElementById('qr-reader')
      if (container) container.innerHTML = ''
      html5QrCode = new Html5Qrcode('qr-reader', {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      })
      await startWithCamera(selectedCameraId.value)
    }

    async function startScanner() {
      await nextTick()
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

      const el = document.getElementById('qr-reader')
      if (!el?.clientWidth) {
        error.value = 'Scanner container not ready.'
        loading.value = false
        return
      }

      html5QrCode = new Html5Qrcode('qr-reader', {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      })

      try {
        const list = await Html5Qrcode.getCameras()
        if (!list?.length) {
          error.value = 'No cameras found.'
          loading.value = false
          return
        }
        cameras.value = list
        const defaultId = await getDefaultCameraId()
        const defaultCam = defaultId && list.find((c) => c.id === defaultId)
        const backByLabel = list.find((c) =>
          /back|rear|environment/i.test(c.label || '')
        )
        const chosen = defaultCam || backByLabel || list[0]
        selectedCameraId.value = chosen.id
        await startWithCamera(chosen.id)
      } catch (err) {
        error.value = err?.message || 'Could not list cameras.'
        loading.value = false
      }
    }

    onMounted(startScanner)

    onBeforeUnmount(() => {
      if (html5QrCode?.isScanning) {
        html5QrCode.stop().catch(() => {})
      }
    })

    return {
      loading,
      error,
      cameras,
      selectedCameraId,
      identifiedId,
      onCameraChange,
      closeEditModal,
    }
  },
}
</script>

<style scoped>
.qr-scanner-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-scanner-container {
  width: 100%;
  min-width: 280px;
  max-width: 400px;
  position: relative;
}
</style>
